import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { Button } from '../primitives/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../primitives/dialog';
import { cn } from '../utils';

export interface InlineAIPanelAction {
  label: string;
  prompt: string;
}

export interface InlineAIPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityContext: string;
  title: string;
  actions: InlineAIPanelAction[];
  onQuery: (query: string) => Promise<string>;
}

export function InlineAIPanel({ open, onOpenChange, title, actions, onQuery }: InlineAIPanelProps) {
  const [customQuery, setCustomQuery] = React.useState('');
  const [response, setResponse] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  function handleClose(nextOpen: boolean) {
    if (!nextOpen) {
      setCustomQuery('');
      setResponse(null);
      setError(null);
    }
    onOpenChange(nextOpen);
  }

  async function runQuery(query: string) {
    if (!query.trim() || isLoading) return;
    setIsLoading(true);
    setError(null);
    setResponse(null);
    try {
      const result = await onQuery(query.trim());
      setResponse(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }

  function handleCustomSubmit(e: React.FormEvent) {
    e.preventDefault();
    void runQuery(customQuery);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-[14px] font-semibold text-[var(--color-text-primary)]">
            {title}
          </DialogTitle>
        </DialogHeader>

        {/* Quick-action pills */}
        {actions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                disabled={isLoading}
                onClick={() => void runQuery(action.prompt)}
                className={cn(
                  'inline-flex items-center rounded-full border border-[var(--color-border)]',
                  'px-3 py-1 text-[12px] text-[var(--color-text-secondary)]',
                  'transition-colors hover:bg-[var(--color-surface)] hover:text-[var(--color-text-primary)]',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Custom query form */}
        <form onSubmit={handleCustomSubmit} className="flex flex-col gap-2">
          <textarea
            value={customQuery}
            onChange={(e) => setCustomQuery(e.target.value)}
            placeholder="Ask something specific…"
            rows={3}
            disabled={isLoading}
            className={cn(
              'resize-none rounded-[var(--radius)] border border-[var(--color-border)]',
              'bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text-primary)]',
              'placeholder:text-[var(--color-text-tertiary)]',
              'focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          />
          <Button
            type="submit"
            size="sm"
            disabled={!customQuery.trim() || isLoading}
            className="self-end"
          >
            {isLoading && <Loader2 size={13} className="animate-spin" aria-hidden />}
            Ask Claude
          </Button>
        </form>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center gap-2 text-[12px] text-[var(--color-text-tertiary)]">
            <Loader2 size={13} className="animate-spin" aria-hidden />
            Thinking…
          </div>
        )}

        {/* Error */}
        {error && !isLoading && <p className="text-[12px] text-[var(--color-error)]">{error}</p>}

        {/* Response */}
        {response && !isLoading && (
          <div
            className={cn(
              'max-h-64 overflow-y-auto rounded-[var(--radius)]',
              'bg-[var(--color-surface)] border border-[var(--color-border)]',
              'px-3 py-2 text-[13px] text-[var(--color-text-primary)]',
              'whitespace-pre-wrap font-mono leading-relaxed',
            )}
          >
            {response}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
